import cloneDeep from 'lodash/cloneDeep';
import IntervalTree from 'node-interval-tree';
import { getOffsets, getUpdatedTimedTranscript, LINE_BREAK_CHARACTER } from './transcriptUtils';
import TranscriptMarker from './transcriptMarker';
import type { Paragraph, SelectedWord } from './transcriptParagraph';
import TranscriptParagraph, { findWordRangeFromParagraph } from './transcriptParagraph';
import type TranscriptParametricRule from './transcriptParametricRule';
import type TranscriptWord from './transcriptWord';
import Subtitle from './subtitle';
import { getMergedRuleSettings, getParametricRules, ParagraphRuleProperties, paragraphRulesSettings, SubtitleRuleProperties, subtitleRulesSettings } from './transcriptParametricRules';
import type { LineBreakIndex } from './lineBreakMarker';
import LineBreakMarker, { isInstanceOfLineBreakMarker } from './lineBreakMarker';
import type { TenantConfigDto } from '../dto/tenantModel';

export enum TranscriptViewType {
  Paragraphs,
  Subtitles,
}

export interface Transcript {
  paragraphs: Paragraph[];
}

const millisecondsToVttTime = (milliseconds: number) => {
  let seconds = milliseconds / 1000.0;
  let hours = 0;
  let minutes = 0;
  hours = Math.floor(seconds / 3600);
  seconds -= hours * 3600;
  minutes = Math.floor(seconds / 60);
  const secondsString = (seconds - minutes * 60).toFixed(3);

  const result =
    hours.toString().padStart(2, '0') +
    ':' +
    minutes.toString().padStart(2, '0') +
    ':' +
    secondsString.padStart(6, '0');

  return result;
};

const supportedFrameRates: ReadonlySet<number> = new Set([23.976, 24, 25, 29.97, 30, 50, 59.94, 60]);
const MINIMUM_SUBTITLE_CUE_DURATION_IN_MS = 1500;

export default class TimedTranscript implements Transcript {
  private _paragraphs: TranscriptParagraph[] = [];
  private _transcriptViewType: TranscriptViewType = TranscriptViewType.Paragraphs;
  private paragraphIntervalTree!: any;
  private uneditedParagraphMarkers: TranscriptMarker[];
  private uneditedSubtitleMarkers: TranscriptMarker[];
  private uneditedTimedTranscript: any[];
  public timecode?: string;
  public mediaFrameRate?: number;

  public paragraphMarkers: TranscriptMarker[] = [];
  public subtitleMarkers: TranscriptMarker[] = [];

  public uneditedParagraphRules: TranscriptParametricRule[] = [];
  public uneditedSubtitleRules: TranscriptParametricRule[] = [];

  public paragraphRules: TranscriptParametricRule[] = [];
  public subtitleRules: TranscriptParametricRule[] = [];

  /**
   * Interval tree keeps a reference to all the paragraphs, including all the words inside the paragraph object.
   * These references prevent the VueComponents from cleaning up properly so we have to null them manually.
   */
  public cleanup() {
    for (const p of this._paragraphs) {
      p.cleanup();
    }
    this.paragraphIntervalTree = undefined;
  }

  public constructor(
    timedTranscript: any[],
    paragraphMarkers: TranscriptMarker[] = [],
    subtitleMarkers: TranscriptMarker[] = [],
    paragraphRules: TranscriptParametricRule[] = [],
    subtitleRules: TranscriptParametricRule[] = [],
    timecode?: string,
    mediaFrameRate?: number,
    showParagraphView = true
  ) {

    this.uneditedTimedTranscript = cloneDeep(timedTranscript);

    this.paragraphMarkers = paragraphMarkers;
    this.subtitleMarkers = subtitleMarkers;

    this.paragraphRules = paragraphRules;
    this.subtitleRules = subtitleRules;

    if (!paragraphMarkers || paragraphMarkers.length === 0) {
      const activeParagraphRules = this.paragraphRules.filter((rule: TranscriptParametricRule) => rule.isActive);
      if (activeParagraphRules.length > 0) {
        this.paragraphMarkers = TimedTranscript.getMarkersFromRules(timedTranscript, this.paragraphRules);
      }
    } else {
      this.disableParagraphRules();
    }

    if (!subtitleMarkers || subtitleMarkers.length === 0) {
      const activeSubtitleRules = this.subtitleRules.filter((rule: TranscriptParametricRule) => rule.isActive);
      if (activeSubtitleRules.length > 0 && subtitleMarkers.length === 0) {
        this.subtitleMarkers = TimedTranscript.getMarkersFromRules(timedTranscript, this.subtitleRules);
      }
    } else {
      this.disableSubtitleRules();
    }

    this.uneditedParagraphRules = cloneDeep(this.paragraphRules);
    this.uneditedSubtitleRules = cloneDeep(this.subtitleRules);
    this.uneditedParagraphMarkers = cloneDeep(this.paragraphMarkers);
    this.uneditedSubtitleMarkers = cloneDeep(this.subtitleMarkers);

    if (mediaFrameRate && supportedFrameRates.has(mediaFrameRate)) {
      this.timecode = timecode;
      this.mediaFrameRate = mediaFrameRate;
    }

    if (showParagraphView) {
      this.paragraphs = this.createParagraphs(timedTranscript, this.paragraphMarkers);
    } else {
      this.paragraphs = this.createParagraphs(timedTranscript, this.subtitleMarkers);
    }
  }

  static convertToVtt(timedTranscript: TranscriptWord[], markers: TranscriptMarker[], wordSeparator: string): string {
    const segments: TranscriptWord[][] = [];
    let line = [];
    let start: number = 0;
    let end: number = 0;
    const lineBreakIndex: LineBreakIndex = {};
    for (const marker of markers) {
      if (isInstanceOfLineBreakMarker(marker)) {
        lineBreakIndex[marker.p] = true;
        continue;
      }
      end = marker.p;
      line = timedTranscript.slice(start, end);
      start = end;
      if (line.length > 0) {
        segments.push(line);
      }
    }

    let vttFileContents = 'WEBVTT\n\n';
    let wordIndex = -1;
    segments.forEach((s: TranscriptWord[], i: number) => {
      const currentSegmentStartTime = s[0].startTime;
      let currentSegmentEndTime = s[s.length - 1].endTime;
      const currentSegmentDuration = currentSegmentEndTime - currentSegmentStartTime;

      const nextSegment = segments[i + 1];
      if (nextSegment && nextSegment[0] && currentSegmentDuration < MINIMUM_SUBTITLE_CUE_DURATION_IN_MS) {
        const diff = MINIMUM_SUBTITLE_CUE_DURATION_IN_MS - currentSegmentDuration;
        currentSegmentEndTime = currentSegmentEndTime + diff > nextSegment[0].startTime ? nextSegment[0].startTime : currentSegmentEndTime + diff;
      }

      vttFileContents += `${i + 1}\n`;
      vttFileContents += `${millisecondsToVttTime(currentSegmentStartTime)} --> ${millisecondsToVttTime(currentSegmentEndTime - 1)}\n`;
      s.forEach((t: TranscriptWord, index: number) => {
        if (t.content?.trim() === LINE_BREAK_CHARACTER) {
          return;
        }
        wordIndex++;
        return vttFileContents += `${t.content}${wordSeparator}${(index !== s.length - 1 && lineBreakIndex[wordIndex]) ? '\n' : ''}`;
      });
      vttFileContents += '\n\n';
    });

    return vttFileContents;
  }

  static getMarkersFromRules(timedTranscript: TranscriptWord[], rules: TranscriptParametricRule[]): TranscriptMarker[] {
    const subtitle = new Subtitle();
    const lineBreakCue = new Subtitle();
    const markers: TranscriptMarker[] = [];
    let marker = 0;

    for (let transcriptIndex = 0; transcriptIndex < timedTranscript.length; transcriptIndex++) {
      const cur = timedTranscript[transcriptIndex];
      if (transcriptIndex !== 0) {
        const prev = timedTranscript[transcriptIndex - 1];
        let segmentationRuleTriggered = false;
        let lineBreakRuleTriggered = false;
        for (let ruleIndex = 0; ruleIndex < rules.length; ruleIndex++) {
          const rule = rules[ruleIndex];
          if (rule.isActive) {
            if (rule.property === String(SubtitleRuleProperties.LINE_BREAK_MAX_CHARACTERS)) {
              if (rule.checkRule(lineBreakCue.currentCue, prev, cur, rule.value)) {
                lineBreakRuleTriggered = true;
              }
              continue;
            }
            if (!segmentationRuleTriggered) {
              if (rule.checkRule(subtitle.currentCue, prev, cur, rule.value)) {
                segmentationRuleTriggered = true;
              }
            }

            if (lineBreakRuleTriggered && segmentationRuleTriggered) {
              break;
            }
          }
        }

        if (segmentationRuleTriggered) {
          marker += subtitle.currentCue.words.length;
          markers.push(new TranscriptMarker(marker));
          subtitle.addNewCue();
          lineBreakCue.addNewCue();
        } else if (lineBreakRuleTriggered) {
          markers.push(new LineBreakMarker(transcriptIndex));
          lineBreakCue.addNewCue();
        }
      }
      subtitle.addWordToCurrentCue(cur);
      lineBreakCue.addWordToCurrentCue(cur);
    }
    // Adding last marker
    markers.push(new TranscriptMarker(timedTranscript.length));
    return markers;
  }

  public areParagraphRulesActive(): boolean {
    return this.paragraphRules.filter((rule: TranscriptParametricRule) => rule.isActive).length > 0;
  }

  public areSubtitleRulesActive(): boolean {
    return this.subtitleRules.filter((rule: TranscriptParametricRule) => rule.isActive).length > 0;
  }

  public disableActiveViewRules() {
    if (this.transcriptViewType === TranscriptViewType.Paragraphs) {
      this.disableParagraphRules();
    } else if (this.transcriptViewType === TranscriptViewType.Subtitles) {
      this.disableSubtitleRules();
    }
  }

  public disableParagraphRules() {
    this.paragraphRules = this.paragraphRules.map((rule: TranscriptParametricRule) => {
      return { ...rule, isActive: false };
    });
  }

  public disableSubtitleRules() {
    this.subtitleRules = this.subtitleRules.map((rule: TranscriptParametricRule) => {
      return { ...rule, isActive: false };
    });
  }

  public getParagrapRulesParameters() {
    return this.getRulesParameters(this.paragraphRules);
  }

  public getSubtitleRulesParameters() {
    return this.getRulesParameters(this.subtitleRules);
  }

  private getRulesParameters(rules: TranscriptParametricRule[]): { [property: string]: { value: number, isActive: boolean}} {
    return rules.reduce(
      (acc: any, cur: TranscriptParametricRule) => {
        acc[cur.property] = {
          value: cur.value,
          isActive: cur.isActive,
        };
        return acc;
      },
      {},
    );
  }

  public updateParagraphMarkers(m: TranscriptMarker[] = []) {
    this.paragraphMarkers = m;
    this.paragraphs = this.createParagraphs(this.flatten(), m);
  }

  public updateSubtitleMarkers(m: TranscriptMarker[] = []) {
    this.subtitleMarkers = m;
    this.paragraphs = this.createParagraphs(this.flatten(), m);
  }

  get paragraphs() {
    return this._paragraphs;
  }

  set paragraphs(paragraphs: TranscriptParagraph[]) {
    this._paragraphs = paragraphs;
    this.buildParagraphIntervalTree();
  }

  get transcriptViewType(): TranscriptViewType {
    return this._transcriptViewType;
  }

  set transcriptViewType(transcriptViewType: TranscriptViewType) {
    this._transcriptViewType = transcriptViewType;
    const tt = this.flatten();
    if (transcriptViewType === TranscriptViewType.Paragraphs) {
      let markers = this.paragraphMarkers;
      const activeParagraphRules = this.paragraphRules.filter((rule: TranscriptParametricRule) => rule.isActive);
      if (activeParagraphRules.length > 0) {
        markers = TimedTranscript.getMarkersFromRules(tt, this.paragraphRules);
      }
      this.paragraphs = this.createParagraphs(tt, markers);
    } else if (transcriptViewType === TranscriptViewType.Subtitles) {
      let markers = this.subtitleMarkers;
      const activeSubtitleRules = this.subtitleRules.filter((rule: TranscriptParametricRule) => rule.isActive);
      if (activeSubtitleRules.length > 0) {
        markers = TimedTranscript.getMarkersFromRules(tt, this.subtitleRules);
      }
      this.paragraphs = this.createParagraphs(tt, markers);
    }
  }

  public addParagraph(paragraph: TranscriptParagraph, reindex: boolean = true) {
    this._paragraphs.push(paragraph);
    if (reindex) {
      this.buildParagraphIntervalTree();
    }
  }

  public getParagraphByTime(time: number): TranscriptParagraph | undefined {
    let paragraph = undefined;
    for (let i = 0; i < this.paragraphs.length; i++) {
      const p = this.paragraphs[i];
      if (time >= p.startTime && p.endTime > time) {
        paragraph = p;
        break;
      }
    }
    return paragraph;
  }

  public getIndexOfParagraph(paragraph: TranscriptParagraph): number {
    return this.paragraphs.indexOf(paragraph);
  }

  public setUneditedTranscript(
    timedTranscript: any[],
    paragraphMarkers: TranscriptMarker[],
    subtitleMarkers: TranscriptMarker[],
    paragraphRules: TranscriptParametricRule[],
    subtitleRules: TranscriptParametricRule[]
  ) {
    this.uneditedTimedTranscript = cloneDeep(timedTranscript);
    this.uneditedParagraphMarkers = cloneDeep(paragraphMarkers);
    this.uneditedSubtitleMarkers = cloneDeep(subtitleMarkers);
    this.uneditedParagraphRules = cloneDeep(paragraphRules);
    this.uneditedSubtitleRules = cloneDeep(subtitleRules);
  }

  public splitParagraph(cur: TranscriptParagraph, characterOffset: number, wordSeparator = ' '): boolean {
    const raw = cur.getRaw();
    const first = raw.substring(0, characterOffset);
    const second = raw.substring(characterOffset);

    // New paragraphs needs at least one word
    if (!first || !second) {
      return false;
    }

    const selection = getOffsets();
    const range = findWordRangeFromParagraph(selection, cur, wordSeparator);
    const curWord = range[0].timed;
    const wordIndex = cur.words.indexOf(curWord);

    // Need at least on word
    const remainingWords = cur.words.slice(0, wordIndex === 0 ? 1 : wordIndex);
    const remainingWordsParagraph = new TranscriptParagraph(remainingWords, this.timecode, this.mediaFrameRate);
    remainingWordsParagraph.update(first);

    const newParagraphWords = cur.getSubWords(wordIndex);
    const newParagraph = new TranscriptParagraph(newParagraphWords, this.timecode, this.mediaFrameRate);
    newParagraph.update(second);

    const indexOfCurrentParagraph = this.paragraphs.indexOf(cur);
    this._paragraphs = [
      ...this.paragraphs.slice(0, indexOfCurrentParagraph),
      remainingWordsParagraph,
      newParagraph,
      ...this.paragraphs.slice(indexOfCurrentParagraph + 1),
    ];

    this.buildParagraphIntervalTree();
    this.updateMarkers();
    return true;
  }

  public mergeParagraphs(indexOfCurrentParagraph: number, indexOfTargetParagraph: number) {
    const target = this.paragraphs[indexOfTargetParagraph];
    const current = this.paragraphs[indexOfCurrentParagraph];
    const mergedParagraphWords = [...target.words, ...current.words];
    const newParagraph = new TranscriptParagraph(mergedParagraphWords, this.timecode, this.mediaFrameRate);
    const updatedParagraphs = this._paragraphs.filter((p: TranscriptParagraph, index: number) => indexOfCurrentParagraph !== index);
    this._paragraphs = [
      ...updatedParagraphs.slice(0, indexOfTargetParagraph),
      newParagraph,
      ...updatedParagraphs.slice(indexOfTargetParagraph + 1),
    ];
    this.buildParagraphIntervalTree();
    this.updateMarkers();
  }

  public mergeCurrentParagraphWithNextFromOffset(current: TranscriptParagraph, characterOffset: number, wordSeparator = ' ') {
    const indexOfCurrentParagraph = this.paragraphs.indexOf(current);
    const next = this.paragraphs[indexOfCurrentParagraph + 1];

    const firstPart = current.getRaw().substring(0, characterOffset);
    const rest = current.getRaw().substring(characterOffset);


    const selection = getOffsets();
    const range = findWordRangeFromParagraph(selection, current, wordSeparator);
    const curWord = range[0].timed;
    let wordIndex = current.words.indexOf(curWord);

    const wordCount = firstPart.split(' ');
    if (firstPart !== '' && wordCount.length === 1) {
      wordIndex = 1;
    }

    const remainingWords = current.words.slice(0, wordIndex);
    let remainingWordsParagraph;
    if (remainingWords.length > 0) {
      remainingWordsParagraph = new TranscriptParagraph(remainingWords, this.timecode, this.mediaFrameRate);
      remainingWordsParagraph.update(firstPart);
    }
    const newParagraphWords = [...current.getSubWords(wordIndex), ...next.words];
    const newParagraph = new TranscriptParagraph(newParagraphWords, this.timecode, this.mediaFrameRate);
    newParagraph.update(rest + ' ' + next.getRaw());

    if (remainingWordsParagraph) {
      this._paragraphs = [
        ...this.paragraphs.slice(0, indexOfCurrentParagraph),
        remainingWordsParagraph,
        newParagraph,
        ...this.paragraphs.slice(indexOfCurrentParagraph + 2),
      ];
    } else {
      this._paragraphs = [
        ...this.paragraphs.slice(0, indexOfCurrentParagraph),
        newParagraph,
        ...this.paragraphs.slice(indexOfCurrentParagraph + 2),
      ];
    }
    this.buildParagraphIntervalTree();
    this.updateMarkers();
  }

  public getActiveParagraphs(startTime: number, endTime: number): { paragraph: TranscriptParagraph, words: TranscriptWord[] }[] {
    const paragraphs = this.paragraphIntervalTree.search(startTime, endTime);
    return paragraphs.map((p: TranscriptParagraph) => {
      return {
        paragraph: p,
        words: p.getWordsByTime(startTime, endTime)
      };
    });
  }

  public getRaw(): string {
    return this.paragraphs.reduce((acc: string, cur: TranscriptParagraph) => {
      return `${acc} ${cur.words.reduce((accW: string, curW: any) => `${accW} ${curW.content}`, '').trim()}`;
    }, '');
  }

  public flatten(): TranscriptWord[] {
    return this.paragraphs.flatMap((p: TranscriptParagraph) => p.words);
  }

  public update(editedText: string, markers: TranscriptMarker[]) {
    if (this.transcriptViewType === TranscriptViewType.Paragraphs) {
      this.paragraphMarkers = markers;
    } else if (this.transcriptViewType === TranscriptViewType.Subtitles) {
      this.subtitleMarkers = markers;
    }
    const tt = this.flatten();
    let updatedTT;
    if (!editedText.trim()) {
      // Currently the transcript editor will crash if all text is removed, and
      // fixing this looks to be non-trivial. So as a very hacky workaround before
      // this is improved, we always leave one character remaining in the editor.
      updatedTT = [{
        startTime: tt[0].startTime,
        endTime: tt[0].endTime,
        content: tt[0].content[0],
        edited: true
      }];
    } else {
      updatedTT = getUpdatedTimedTranscript(tt, editedText);
    }
    this.paragraphs = this.createParagraphs(updatedTT, markers);
  }

  public revert() {
    // Need to make copy of the backup, so we don't lose it and edit it
    let paragraphMarkers = cloneDeep(this.uneditedParagraphMarkers);
    let subtitleMarkers = cloneDeep(this.uneditedSubtitleMarkers);
    this.paragraphRules = cloneDeep(this.uneditedParagraphRules);
    this.subtitleRules = cloneDeep(this.uneditedSubtitleRules);

    const activeParagraphRules = this.paragraphRules.filter((rule: TranscriptParametricRule) => rule.isActive);
    if (activeParagraphRules.length > 0) {
      paragraphMarkers = TimedTranscript.getMarkersFromRules(this.uneditedTimedTranscript, this.paragraphRules);
    }
    const activeSubtitleRules = this.subtitleRules.filter((rule: TranscriptParametricRule) => rule.isActive);
    if (activeSubtitleRules.length > 0) {
      subtitleMarkers = TimedTranscript.getMarkersFromRules(this.uneditedTimedTranscript, this.subtitleRules);
    }
    if (this.transcriptViewType === TranscriptViewType.Paragraphs) {
      this.paragraphs = this.createParagraphs(cloneDeep(this.uneditedTimedTranscript), cloneDeep(paragraphMarkers));
    } else if (this.transcriptViewType === TranscriptViewType.Subtitles) {
      this.paragraphs = this.createParagraphs(cloneDeep(this.uneditedTimedTranscript), cloneDeep(subtitleMarkers));
    }
    this.paragraphMarkers = cloneDeep(paragraphMarkers);
    this.subtitleMarkers = cloneDeep(subtitleMarkers);
  }

  private updateMarkers() {
    if (this.transcriptViewType === TranscriptViewType.Paragraphs) {
      this.paragraphMarkers = this.createMarkers();
    } else if (this.transcriptViewType === TranscriptViewType.Subtitles) {
      this.subtitleMarkers = this.createMarkers();
    }
  }

  private createMarkers(): TranscriptMarker[] {
    const paragraphMarkers: TranscriptMarker[] = [];
    let position = 0;
    this._paragraphs.forEach((p: TranscriptParagraph) => {
      position += p.words.length;
      paragraphMarkers.push(new TranscriptMarker(position));
    });
    return paragraphMarkers;
  }

  private createParagraphs(timedTranscript: any[], markers: TranscriptMarker[]): TranscriptParagraph[] {
    const paragraphs = [];
    let pStart = 0;
    let pEnd = 0;
    let lineBreakIndex: LineBreakIndex = {};
    let nextLineBreakIndex: LineBreakIndex = {};
    for (let index = 0; index < markers.length; index++) {
      const marker = markers[index];
      const nextMarker = markers[index + 1];
      if (isInstanceOfLineBreakMarker(marker)) {
        if (marker.p === nextMarker.p) {
          nextLineBreakIndex[marker.p - nextMarker.p] = true;
        } else {
          lineBreakIndex[marker.p - pStart] = true;
        }
        continue;
      }
      pEnd = marker.p;
      const words = timedTranscript.slice(pStart, pEnd);
      if (words.length > 0) {
        paragraphs.push(new TranscriptParagraph(words, this.timecode, this.mediaFrameRate, lineBreakIndex));
        lineBreakIndex = {...nextLineBreakIndex};
        nextLineBreakIndex = {};
      }
      pStart = pEnd;
    }
    if (timedTranscript.length > pEnd) {
      const remainingWords = timedTranscript.slice(pEnd);
      if (remainingWords.length > 0) {
        paragraphs.push(new TranscriptParagraph(remainingWords, this.timecode, this.mediaFrameRate));
      }
    }
    return paragraphs;
  }

  private buildParagraphIntervalTree() {
    const tree = new IntervalTree();
    this.paragraphs.forEach((p: TranscriptParagraph) => {
      if (p.startTime <= p.endTime)
        tree.insert(p.startTime, p.endTime, p);
    });
    this.paragraphIntervalTree = tree;
  }
}


export const getIndexOfWordByParagraph = (timedTranscript: TimedTranscript, paragraphIndex: number, word: TranscriptWord) => {
  const { paragraphs } = timedTranscript;
  const paragraph = paragraphs[paragraphIndex];
  let indexOfWord = 0;
  if (word) {
    indexOfWord += paragraph.words.indexOf(word);
    if (paragraphIndex !== -1) {
      for (let i = 0; i < paragraphIndex; i++) {
        indexOfWord += paragraphs[i].words.length;
      }
    }
  }

  return indexOfWord;
};


export const findSelectedWords = (
  timedTranscript: TimedTranscript,
  selection: any,
  firstParagraphIndex: number,
  lastParagraphIndex: number,
  wordSeparator = ' ',
) => {
  const selectedWords: SelectedWord[] = [];
  if (!selection || firstParagraphIndex === -1 || lastParagraphIndex === -1) {
    return selectedWords;
  }
  for (
    let i = firstParagraphIndex;
    i <= firstParagraphIndex + (lastParagraphIndex - firstParagraphIndex);
    i += 1
  ) {
    const p = timedTranscript.paragraphs[i];
    if (p) {
      const raw = p.getRaw(wordSeparator);
      let startOffset = selection.startOffset;
      let endOffset = selection.endOffset;
      if (
        i === firstParagraphIndex &&
        firstParagraphIndex !== lastParagraphIndex
      ) {
        endOffset = raw.length;
      } else if (
        i ===
          firstParagraphIndex + (lastParagraphIndex - firstParagraphIndex) &&
        firstParagraphIndex !== lastParagraphIndex
      ) {
        startOffset = 0;
      } else if (i !== firstParagraphIndex) {
        startOffset = 0;
        endOffset = raw.length;
      }
      const wordRange = findWordRangeFromParagraph(
        { startOffset: startOffset, endOffset: endOffset },
        p,
        wordSeparator,
      );
      selectedWords.push(...wordRange);
    }
  }
  return selectedWords;
};


export function handleTimedTranscript(
  timedTranscript: any,
  paragraphMarkers: any[] = [],
  subtitleMarkers: any[] = [],
  tenantConfig: TenantConfigDto,
  paragraphRulesValues: any = undefined,
  subtitleRulesValues: any = undefined,
  timecode?: string,
  mediaFrameRate?: number,
) {
  // Sometimes words can come with only start time and end time and no content.
  const sanitized = timedTranscript.filter((t: any) => t.content && t.content !== LINE_BREAK_CHARACTER);

  const pMarkers = paragraphMarkers.map((marker: any) => new TranscriptMarker(marker.p));
  const sMarkers = subtitleMarkers.map((marker: any) => marker.lineBreak ? new LineBreakMarker(marker.p) : new TranscriptMarker(marker.p));

  const paragraphSettings = getMergedRuleSettings(tenantConfig.paragraphParametricRules, paragraphRulesSettings, Object.values(ParagraphRuleProperties)).mergedSettings;
  const subtitleSettings = getMergedRuleSettings(tenantConfig.subtitleParametricRules, subtitleRulesSettings, Object.values(SubtitleRuleProperties)).mergedSettings;

  const subtitleRules = getParametricRules(subtitleSettings, subtitleRulesValues);
  const paragraphRules = getParametricRules(paragraphSettings, paragraphRulesValues);

  const frameRate = mediaFrameRate ? parseFloat(mediaFrameRate.toFixed(2)) : undefined;
  return new TimedTranscript(sanitized, pMarkers, sMarkers, paragraphRules, subtitleRules, timecode, frameRate);
}
